import confetti from 'canvas-confetti'
import { format, parseISO } from 'date-fns'
import {
  CheckCircle2Icon,
  ExternalLinkIcon,
  FileQuestionIcon,
  MessageCircleIcon,
  MonitorCheckIcon,
  VideoIcon,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from '#router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { TASK_TYPE_CONFIG } from '../data/task-config'
import { useHomework } from '../providers/homework-provider'
import { BehavioralSubmissionDialog } from './behavioral-submission-dialog'
import { CoachBubble } from './coach-feedback'
import { ReviewCommentKindChip, useCoachingFeedback } from './coaching-feedback'
import { SystemDesignSubmissionDialog } from './system-design-submission-dialog'

// Dates cross the wire as ISO strings (no tRPC transformer), but the router's
// inferred output types call them `Date`. Accept both rather than asserting one
// — the day a transformer is configured, `String(aDate)` would parse to
// Invalid Date and throw out of render.
type WireDate = string | Date

const wireDate = (value: WireDate): Date =>
  value instanceof Date ? value : parseISO(value)

const wireTime = (value: WireDate): number => wireDate(value).getTime()

export function TaskDetailDialog() {
  const { activeTask, closeTask, isCompleted, toggleCompleted } = useHomework()
  // Real review feedback, joined to this card by assignment id. The Coaching
  // notes section below used to be hardcoded empty for canonical-plan tasks —
  // the plan DTO carries no comments, and review feedback rendered only in the
  // separate feedback panel, so a coach's review was invisible from the card
  // it was about.
  const {
    reviews,
    isLoading: feedbackLoading,
    isError: feedbackFailed,
  } = useCoachingFeedback()
  const utils = trpc.useUtils()
  const markViewed = trpc.dashboard.feedback.markViewed.useMutation()

  const task = activeTask
  const config = task ? TASK_TYPE_CONFIG[task.type] : null
  const Icon = config?.icon
  const [submissionDialog, setSubmissionDialog] = useState<
    'system-design' | 'behavioral' | null
  >(null)

  const switchRef = useRef<HTMLDivElement>(null)

  const handleToggle = useCallback(() => {
    if (!task) return
    const wasCompleted = isCompleted(task)
    toggleCompleted(task)

    // Celebrate only when marking done (not un-marking)
    if (wasCompleted) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const rect = switchRef.current?.getBoundingClientRect()
    const origin = rect
      ? {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight,
        }
      : { x: 0.5, y: 0.5 }

    confetti({
      particleCount: 70,
      spread: 65,
      startVelocity: 24,
      scalar: 0.8,
      ticks: 130,
      origin,
      zIndex: 9999,
      disableForReducedMotion: true,
    })
  }, [task, isCompleted, toggleCompleted])

  const taskReviews = task
    ? reviews.filter((review) => review.assignmentId === task.id)
    : []
  // Flattened across attempts, so order by when the client could first see each
  // comment. The server returns reviews by `id` — random UUID order — which
  // would interleave a re-submission's feedback above the original's.
  const reviewComments = taskReviews
    .flatMap((review) => review.comments)
    .sort((left, right) => wireTime(left.visibleAt) - wireTime(right.visibleAt))
  const hasCoachingNotes =
    reviewComments.length > 0 ||
    (task?.comments.length ?? 0) > 0 ||
    Boolean(task?.coachNote)

  // Reading the feedback here is reading it, so clear the unread receipt the
  // Feedback tab's badge counts. Without this a client who only ever opens
  // task cards carries an unread badge that nothing they do can clear.
  // Sorted before joining: the server returns reviews in UUID order, so the
  // same set of unread ids can arrive in a different order across refetches,
  // and an unsorted key would re-fire the effect and re-post every receipt.
  const unreadKey = taskReviews
    .filter((review) => review.unread)
    .map((review) => review.reviewId)
    .sort()
    .join(',')
  useEffect(() => {
    if (unreadKey === '') return
    void Promise.all(
      unreadKey
        .split(',')
        .map((reviewId) => markViewed.mutateAsync({ reviewId })),
    )
      .then(() =>
        // Deliberately not guarded on the effect still being live: this
        // invalidates a query cache, not component state. Skipping it when the
        // dialog closed mid-flight would leave React Query holding
        // `unread: true` for a receipt the server has already recorded, and
        // the Feedback tab's badge wrong until some unrelated refetch.
        utils.dashboard.feedback.list.invalidate(),
      )
      .catch(() => {
        // Best effort: a failed receipt leaves the badge up, which is the
        // status quo, not a regression.
      })
    // `markViewed`/`utils` are stable tRPC handles; keying on the id list means
    // one pass per distinct set of unread reviews on screen.
  }, [unreadKey, markViewed.mutateAsync, utils])

  return (
    <Dialog open={task !== null} onOpenChange={(open) => !open && closeTask()}>
      {/* Wider than the original sm:max-w-lg so notes have room to breathe;
          every section is conditional, so a card with none of them still
          renders as a compact dialog. */}
      <DialogContent className="max-h-[85vh] gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        {task && config && Icon && (
          <>
            <DialogHeader className="gap-3 p-5 pb-4">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex size-8 items-center justify-center rounded-md',
                    config.chip,
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <Badge variant="secondary">{config.label}</Badge>
                {task.difficulty && (
                  <Badge variant="outline">{task.difficulty}</Badge>
                )}
              </div>
              <DialogTitle className="text-lg text-balance">
                {task.title}
              </DialogTitle>
              <DialogDescription>
                Assigned for {format(parseISO(task.date), 'EEEE, MMMM d, yyyy')}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-5 px-5 pb-5">
              {/* Completion */}
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-3.5 py-3">
                {task.platformTracked ? (
                  <>
                    <div className="flex items-center gap-2.5">
                      <MonitorCheckIcon
                        className="size-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {task.meeting
                            ? 'Attendance tracked'
                            : 'Reviewed by your coach'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {task.meeting
                            ? 'Completes automatically when you attend the meeting'
                            : 'Completes when your coach reviews your submission'}
                        </span>
                      </div>
                    </div>
                    {isCompleted(task) ? (
                      <span className="flex items-center gap-1.5 text-sm font-medium text-task-exercise">
                        <CheckCircle2Icon
                          className="size-4"
                          aria-hidden="true"
                        />
                        Done
                      </span>
                    ) : task.type === 'system-design' ? (
                      <Button
                        size="sm"
                        onClick={() => setSubmissionDialog('system-design')}
                      >
                        Submit for review
                      </Button>
                    ) : task.type === 'leetcode' || task.type === 'exercise' ? (
                      <span className="text-xs text-muted-foreground">
                        Submit from the problem page
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setSubmissionDialog('behavioral')}
                      >
                        Submit for review
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="task-complete"
                      className="text-sm font-medium"
                    >
                      Mark as completed
                    </label>
                    <div ref={switchRef} className="flex items-center">
                      <Switch
                        id="task-complete"
                        checked={isCompleted(task)}
                        onCheckedChange={handleToggle}
                        aria-label="Mark task as completed"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Meeting details */}
              {task.meeting && (
                <div className="flex flex-col gap-2.5 rounded-lg border border-task-meeting/30 bg-task-meeting/5 p-3.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-task-meeting/15 text-task-meeting">
                      {task.meeting.kind === 'peer'
                        ? 'Peer interview'
                        : 'Accountability call'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {format(parseISO(task.date), 'EEE, MMM d')} ·{' '}
                      {task.meeting.time}
                    </span>
                  </div>

                  {task.meeting.kind === 'peer' && (
                    <div className="flex flex-col gap-1 text-sm">
                      <span>
                        <span className="text-muted-foreground">
                          Interviewer:{' '}
                        </span>
                        <span className="font-medium">
                          {task.meeting.role === 'interviewer'
                            ? 'You'
                            : task.meeting.peerName}
                        </span>
                      </span>
                      <span>
                        <span className="text-muted-foreground">
                          Interviewee:{' '}
                        </span>
                        <span className="font-medium">
                          {task.meeting.role === 'interviewer'
                            ? task.meeting.peerName
                            : 'You'}
                        </span>
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" asChild>
                      <Link href={`/interview/${task.meeting.meetingId}`}>
                        <VideoIcon />
                        Join meeting
                      </Link>
                    </Button>
                    {task.meeting.role === 'interviewer' &&
                      task.meeting.suggestedQuestion && (
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={task.meeting.suggestedQuestion.solutionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileQuestionIcon />
                            AI-suggested question + solution
                          </a>
                        </Button>
                      )}
                  </div>
                  {task.meeting.role === 'interviewer' &&
                    task.meeting.suggestedQuestion && (
                      <p className="text-xs text-muted-foreground">
                        The AI suggests asking{' '}
                        <span className="font-medium text-foreground">
                          {task.meeting.suggestedQuestion.title}
                        </span>{' '}
                        — review the solution before the call so you can grade
                        and give hints confidently.
                      </p>
                    )}
                </div>
              )}

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  Details
                </h3>
                <p className="text-sm leading-relaxed text-pretty">
                  {task.description}
                </p>
              </div>

              {/* Universal notes — about the problem itself, not this
                  assignment, so everyone who is ever given it sees them. */}
              {task.universalNotes && (
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                    About this problem
                  </h3>
                  <p className="text-sm leading-relaxed whitespace-pre-line text-pretty">
                    {task.universalNotes}
                  </p>
                </div>
              )}

              {/* Links */}
              {task.links.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                    Links
                  </h3>
                  <div className="flex flex-col gap-1">
                    {task.links.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        <ExternalLinkIcon
                          className="size-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Coaching notes */}
              <div className="flex flex-col gap-2">
                <Separator />
                <h3 className="mt-1 flex items-center gap-1.5 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  <MessageCircleIcon className="size-3.5" aria-hidden="true" />
                  Coaching notes
                </h3>
                {/* The review query's state is reported alongside whatever
                    notes we do have, never instead of them: a task with a
                    coach note but a failed feedback request would otherwise
                    look complete while its review comments were missing. */}
                {feedbackLoading ? (
                  <p className="text-sm text-muted-foreground">
                    Loading feedback…
                  </p>
                ) : feedbackFailed ? (
                  <p className="text-sm text-muted-foreground">
                    Coach feedback could not be loaded. Refresh to try again.
                  </p>
                ) : null}
                {!hasCoachingNotes ? (
                  // Only once the query has settled successfully — otherwise
                  // this claims the coach hasn't reviewed anything when we
                  // simply could not find out.
                  !feedbackLoading &&
                  !feedbackFailed && (
                    <p className="text-sm text-muted-foreground">
                      No coach feedback yet. Notes appear here after your coach
                      reviews this task.
                    </p>
                  )
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {/* The coach's own note comes first — it is the guidance
                        written for this card, and it may predate any work. */}
                    {task.coachNote && (
                      <CoachBubble
                        author="Your coach"
                        text={task.coachNote}
                        meta={
                          <span className="text-xs text-muted-foreground">
                            Note for this task
                          </span>
                        }
                      />
                    )}
                    {reviewComments.map((comment) => (
                      <CoachBubble
                        key={comment.id}
                        author="Your coach"
                        text={comment.body}
                        meta={
                          <>
                            <ReviewCommentKindChip kind={comment.kind} />
                            <span className="text-xs text-muted-foreground">
                              {comment.targetLabel} ·{' '}
                              {format(
                                wireDate(comment.visibleAt),
                                'MMM d, yyyy',
                              )}
                            </span>
                          </>
                        }
                      />
                    ))}
                    {task.comments.map((comment) => (
                      <CoachBubble
                        key={comment.id}
                        author={comment.author}
                        text={comment.text}
                        aspect={comment.aspect}
                        meta={
                          <span className="text-xs text-muted-foreground">
                            {format(parseISO(comment.date), 'MMM d, yyyy')}
                          </span>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
      {task && (
        <>
          <SystemDesignSubmissionDialog
            task={task}
            open={submissionDialog === 'system-design'}
            onOpenChange={(open) =>
              setSubmissionDialog(open ? 'system-design' : null)
            }
          />
          <BehavioralSubmissionDialog
            task={task}
            open={submissionDialog === 'behavioral'}
            onOpenChange={(open) =>
              setSubmissionDialog(open ? 'behavioral' : null)
            }
          />
        </>
      )}
    </Dialog>
  )
}
